// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { OptionGroup } from "../OptionGroup"
import { FreeTextListOrNone } from "../FreeTextListOrNone"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

/**
 * Owner-mandated fix: free numeric minute entry produced unrealistic values
 * against actual recipe active-time data, so per-meal is now a fixed
 * single-select preset. Per-day is derived (never asked separately) as
 * 2.5x the per-meal choice, rounded to the nearest 15 minutes — still
 * written as the two plain numbers the contract expects
 * (packages/contracts/src/schemas/onboarding.ts Step9Schema: both positive
 * ints), so nothing downstream changes.
 */
const PER_MEAL_PRESETS = [15, 25, 40, 60] as const

function perDayFromPerMeal(perMealMinutes: number): number {
  return Math.round((perMealMinutes * 2.5) / 15) * 15
}

/** Step 9 (wire) — time per meal/day (preset, per-day derived) + equipment (explicit-list-or-none). */
export function Step9Time({ value, onChange }: Props) {
  const { t } = useI18n()
  const timePerMealMinutes = (value.timePerMealMinutes as number | undefined) ?? null
  // Raw (possibly-undefined) value renders — an unanswered field must never
  // look like an explicit "None" was already chosen (item 30). "Committed"
  // is only used to re-write this field's own value alongside the derived
  // per-day minutes when the *other* control (the preset) changes.
  const equipmentRaw = value.equipment as string[] | "none" | undefined
  const equipment = equipmentRaw ?? "none"

  const options = PER_MEAL_PRESETS.map(minutes => ({
    value: String(minutes),
    label: t("onboarding.step9.presetUpTo", { minutes }),
  }))

  function selectPreset(minutes: number) {
    onChange({
      timePerMealMinutes: minutes,
      timePerDayMinutes: perDayFromPerMeal(minutes),
      equipment,
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <OptionGroup
        legend={t("onboarding.step9.perMealLabel")}
        columns={2}
        value={timePerMealMinutes !== null ? String(timePerMealMinutes) : null}
        onChange={next => selectPreset(Number(next))}
        options={options}
      />
      <FreeTextListOrNone
        inputId="ob-equipment"
        label={t("onboarding.step9.equipmentLabel")}
        placeholder={t("onboarding.step9.equipmentPlaceholder")}
        noneLabel={t("onboarding.step6.noneLabel")}
        // Owner 2026-08-29: genuinely optional (an untouched field saves as
        // "none") — the star claimed otherwise.
        required={false}
        value={equipmentRaw}
        onChange={next =>
          onChange({
            timePerMealMinutes,
            timePerDayMinutes:
              timePerMealMinutes !== null ? perDayFromPerMeal(timePerMealMinutes) : null,
            equipment: next,
          })
        }
      />
    </div>
  )
}
